import React, { useState } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Sync as SyncIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Button } from 'new/elements/Button';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import { formatRelativeTime } from '../../../utils/dateFormatters';
import {
  useListExtMcpServersQuery,
  useDeleteExtMcpServerMutation,
  useSyncExtMcpServerMutation,
} from '../../../state/aiApi';
import type { ApiExtMcpServer } from '../../../state/aiApiTypes';
import AddExtMcpServerDialog from './AddExtMcpServerDialog';
import EditExtMcpServerDialog from './EditExtMcpServerDialog';
import styles from './GatewaySection.module.scss';

function getServerStatusColor(status: string): 'success' | 'warning' | 'error' | 'disabled' {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'disabled';
    case 'error':
      return 'error';
    case 'auth_error':
      return 'error';
    default:
      return 'disabled';
  }
}

function getServerStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'error':
      return 'Error';
    case 'auth_error':
      return 'Auth Error';
    default:
      return status || 'Unknown';
  }
}

const ServerRow: React.FC<{
  server: ApiExtMcpServer;
  toolsetId: number;
  onEdit: (server: ApiExtMcpServer) => void;
  onDelete: (server: ApiExtMcpServer) => void;
  onSync: (server: ApiExtMcpServer) => void;
  isSyncing: boolean;
}> = ({ server, onEdit, onDelete, onSync, isSyncing }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <div className={styles.serverRow}>
      <div className={styles.serverInfo}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={500}>
            {server.server_name}
          </Typography>
          <ChipBadge
            label={getServerStatusLabel(server.status)}
            color={getServerStatusColor(server.status)}
          />
        </Stack>
        {server.server_description && (
          <Typography variant="caption" color="text.secondary" className={styles.serverDescription}>
            {server.server_description}
          </Typography>
        )}
        {server.server_url && (
          <Typography variant="caption" color="text.secondary" className={styles.serverUrl}>
            {server.server_url}
          </Typography>
        )}
        {server.last_synced_at && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <SyncIcon sx={{ fontSize: 12 }} aria-hidden="true" />
            Last synced {formatRelativeTime(server.last_synced_at)}
            {server.last_sync_duration_ms != null && (
              <>
                {' '}
                (
                {server.last_sync_duration_ms < 1000
                  ? `${server.last_sync_duration_ms}ms`
                  : `${(server.last_sync_duration_ms / 1000).toFixed(1)}s`}
                )
              </>
            )}
          </Typography>
        )}
      </div>
      <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
        <Tooltip title="Sync tools from this server">
          <span>
            <IconButton size="small" onClick={() => onSync(server)} disabled={isSyncing}>
              {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              onEdit(server);
              setAnchorEl(null);
            }}
          >
            <EditIcon fontSize="small" className={styles.menuIcon} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDelete(server);
              setAnchorEl(null);
            }}
            className={styles.deleteMenuItem}
          >
            <DeleteIcon fontSize="small" className={styles.menuIcon} />
            Delete
          </MenuItem>
        </Menu>
      </Stack>
    </div>
  );
};

interface ExtMcpServersPanelProps {
  toolsetId: number;
}

const ExtMcpServersPanel: React.FC<ExtMcpServersPanelProps> = ({ toolsetId }) => {
  const { showSuccess, showError } = useSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editServer, setEditServer] = useState<ApiExtMcpServer | null>(null);
  const [syncingIds, setSyncingIds] = useState<Set<number>>(new Set());

  const { data: servers = [], isLoading } = useListExtMcpServersQuery({ toolsetId });
  const [deleteServer] = useDeleteExtMcpServerMutation();
  const [syncServer] = useSyncExtMcpServerMutation();

  const handleDelete = (server: ApiExtMcpServer) => {
    confirm({
      title: 'Delete MCP Server',
      message: `Delete "${server.server_name}"? All tools synced from this server will also be removed.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteServer({ toolsetId, serverId: server.id }).unwrap();
          showSuccess('Server deleted');
        } catch (err: any) {
          showError(err?.data?.message || 'Failed to delete server');
        }
      },
    });
  };

  const handleSync = async (server: ApiExtMcpServer) => {
    setSyncingIds((prev) => new Set(prev).add(server.id));
    try {
      await syncServer({ toolsetId, serverId: server.id }).unwrap();
      showSuccess(`Sync triggered for "${server.server_name}"`);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to trigger sync');
    } finally {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(server.id);
        return next;
      });
    }
  };

  return (
    <div className={styles.panel}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle2" className={styles.panelTitle}>
          External MCP Servers
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Server
        </Button>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : servers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" className={styles.emptyText}>
          No external MCP servers registered. Add one to sync tools from a remote MCP server.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {servers.map((server) => (
            <ServerRow
              key={server.id}
              server={server}
              toolsetId={toolsetId}
              onEdit={setEditServer}
              onDelete={handleDelete}
              onSync={handleSync}
              isSyncing={syncingIds.has(server.id)}
            />
          ))}
        </Stack>
      )}

      <AddExtMcpServerDialog
        open={addDialogOpen}
        toolsetId={toolsetId}
        onClose={() => setAddDialogOpen(false)}
      />

      {editServer && (
        <EditExtMcpServerDialog
          open
          toolsetId={toolsetId}
          server={editServer}
          onClose={() => setEditServer(null)}
        />
      )}

      {ConfirmDialogComponent}
    </div>
  );
};

export default ExtMcpServersPanel;
